import { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Info, AlertCircle, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { getBrandPulse, listGeos, type BrandPulseResponse, type SupportedCountry, type SupportedRegion } from '@/api/brandPulseApi';

/**
 * Small info-icon + tooltip helper. Used to explain every metric on the page.
 * Source material for all explanations is BRAND_PULSE_METHODOLOGY.md; copy
 * here is deliberately concise for the hover surface.
 */
function MetricInfo({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-gray-300 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-full"
          aria-label={title ? `About ${title}` : 'More info'}
          onClick={(e) => e.preventDefault()}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="max-w-xs bg-gray-900 text-white text-xs leading-relaxed p-3"
      >
        {title && <div className="font-semibold mb-1">{title}</div>}
        <div className="text-gray-200">{children}</div>
      </TooltipContent>
    </UITooltip>
  );
}

/**
 * Hardcoded default config. In production this would come from a brand selector
 * / team context. For now the focal brand & competitor cohort are defined here.
 */
const DEFAULT_CONFIG = {
  brand: 'Merrell',
  competitors: ['The North Face', 'Salomon', 'Columbia', 'KEEN'],
  category: 'hiking boots',
  geo: 'US',
};

// Distinct, readable colors for each brand line. Focal brand = Beatly purple.
const BRAND_COLORS: Record<string, string> = {
  Merrell: '#be43ff',
  'The North Face': '#0f172a',
  Salomon: '#2563eb',
  Columbia: '#f59e0b',
  KEEN: '#059669',
};

/**
 * Brands whose search volume is known to be polluted by non-category queries
 * (e.g. "Columbia" catches the country, university, etc). These are still
 * plotted but with a dashed line style to signal measurement noise.
 */
const DEFAULT_AMBIGUOUS = new Set<string>(['Columbia']);

function pct(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

function delta(n: number, digits = 1): { label: string; positive: boolean } {
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return { label: `${sign}${Math.abs(n).toFixed(digits)} pts`, positive: n >= 0 };
}

export function BrandPulseBrandTracker() {
  const [data, setData] = useState<BrandPulseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geo, setGeo] = useState<string>(DEFAULT_CONFIG.geo);
  const [geoOptions, setGeoOptions] = useState<{ countries: SupportedCountry[]; regions: SupportedRegion[] } | null>(null);
  const [hiddenBrands, setHiddenBrands] = useState<Set<string>>(() => new Set());
  const [ambiguousBrands, setAmbiguousBrands] = useState<Set<string>>(
    () => new Set(DEFAULT_AMBIGUOUS)
  );

  const toggleHidden = (brand: string) => {
    setHiddenBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };
  const toggleAmbiguous = (brand: string) => {
    setAmbiguousBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  // One-time: fetch the supported country/region list for the picker.
  useEffect(() => {
    listGeos().then(setGeoOptions).catch(() => { /* picker degrades to free text */ });
  }, []);

  // Refetch whenever the selected geo (country code or region key) changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getBrandPulse({ ...DEFAULT_CONFIG, geo })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e.message ?? String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [geo]);

  const focalBrand = DEFAULT_CONFIG.brand;

  const focalSoS = data?.shareOfSearch.brands.find((b) => b.brand === focalBrand);
  const focalSentiment = data?.sentiment.find((s) => s.brand === focalBrand);
  const focalCatFit = data?.categoryFit.find((c) => c.brand === focalBrand);
  const focalComp = data?.comparisonIntent.find((c) => c.brand === focalBrand);
  const focalBps = data?.compositeScores.find((c) => c.brand === focalBrand);
  const focalAssoc = data?.associations.find((a) => a.brand === focalBrand);

  const kpiData = useMemo(() => {
    if (!data || !focalSoS || !focalSentiment || !focalCatFit || !focalComp || !focalBps) return [];
    const sosDelta = focalSoS.momentum;
    const sosSparkline = focalSoS.series.slice(-8).map((s) => s.value);

    return [
      {
        label: 'Share of Search',
        value: pct(focalSoS.trailing4w),
        delta: delta(sosDelta),
        info: (
          <>
            Brand's share of total category search interest across the cohort, computed
            weekly from Google Trends (trailing 4-week mean).
            <br /><br />
            <strong>Why it matters:</strong> Share of Search is a validated leading
            indicator of market share (Binet & Hankins, 2020; r ≈ 0.7–0.85 across
            tested categories, lead ~6–12 months).
            <br /><br />
            <strong>Caveat:</strong> ambiguous brand names (e.g. "Columbia") can be
            polluted by non-category queries — flagged with a dashed line in the chart.
          </>
        ),
        sparkline: sosSparkline,
      },
      {
        label: 'Category Fit',
        value: pct(focalCatFit.score, 0),
        delta: { label: 'Mental availability', positive: true },
        info: (
          <>
            Share of top category search queries where the brand surfaces in Google's
            top-10 organic, "People Also Ask", or Knowledge Panel.
            <br /><br />
            <strong>Why it matters:</strong> passive proxy for Ehrenberg-Bass "mental
            availability" — the probability a brand is thought of at buying moments.
            High score = brand shows up when category intent is expressed.
          </>
        ),
        sparkline: [focalCatFit.score, focalCatFit.score],
      },
      {
        label: 'Comparison Intent',
        value: pct(focalComp.combined, 0),
        delta: { label: 'Preference proxy', positive: true },
        info: (
          <>
            Relative Google-Trends search volume for preference-signalling queries:
            "<em>brand</em> vs", "<em>brand</em> review", and "best <em>category</em>".
            Indexed 0–100 across the cohort.
            <br /><br />
            <strong>Why it matters:</strong> a behavioural proxy for preference/
            consideration that doesn't require surveys — measures active shopper
            research, not just awareness.
          </>
        ),
        sparkline: [focalComp.combined, focalComp.combined],
      },
      {
        label: 'Brand Pulse Score',
        value: Math.round(focalBps.bpsIndex).toString(),
        delta: { label: `${Math.round(focalBps.confidence * 100)}% confidence`, positive: focalBps.confidence >= 0.6 },
        info: (
          <>
            Composite index (0–100) across <strong>five pillars</strong>, each
            built from cohort z-scores and rescaled so 50 = cohort average.
            <br /><br />
            <strong>Pillars &amp; default weights:</strong> Awareness 30% (Share
            of Search + Wikipedia + YouTube + GDELT), Mental Availability 20%
            (Category Fit), Consideration 20% (Comparison Intent), Sentiment
            15% (News + GDELT tone), Engagement 15% (YouTube views/likes/
            comments). Weights re-normalize when a pillar has no data.
            <br /><br />
            <strong>Confidence</strong> = fraction of pillars where the cohort
            shows variance. Below 60% means at least two pillars are flat —
            score is directional only.
          </>
        ),
        sparkline: [focalBps.bpsIndex, focalBps.bpsIndex],
      },
    ];
  }, [data, focalSoS, focalSentiment, focalCatFit, focalComp, focalBps]);

  const momentumData = useMemo(() => {
    if (!data) return [];
    // Convert weekly panel into chart-ready rows keyed by normalized brand names.
    return data.shareOfSearch.weeklyPanel.map((p) => {
      const monthLabel = new Date(p.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const row: Record<string, string | number> = { date: monthLabel };
      const total = Object.values(p.values).reduce((a, b) => a + b, 0);
      for (const brand of [DEFAULT_CONFIG.brand, ...DEFAULT_CONFIG.competitors]) {
        const raw = p.values[brand.toLowerCase()] ?? 0;
        row[brand] = total > 0 ? (raw / total) * 100 : 0;
      }
      return row;
    });
  }, [data]);

  const competitorBenchmark = useMemo(() => {
    if (!data) return [];
    return data.shareOfSearch.brands
      .slice()
      .sort((a, b) => b.trailing4w - a.trailing4w)
      .map((b) => ({
        brand: b.brand,
        value: Number(b.trailing4w.toFixed(1)),
        isHighlighted: b.brand === focalBrand,
      }));
  }, [data, focalBrand]);

  const sentimentMixData = useMemo(() => {
    if (!focalSentiment || focalSentiment.sampleSize === 0) return [];
    return [
      { name: 'Positive', value: focalSentiment.positive, color: '#10b981' },
      { name: 'Neutral', value: focalSentiment.neutral, color: '#6b7280' },
      { name: 'Negative', value: focalSentiment.negative, color: '#ef4444' },
    ];
  }, [focalSentiment]);

  const driversOfChange = useMemo(() => {
    if (!focalAssoc) return { positive: [], negative: [] };
    const rising = focalAssoc.rising.slice(0, 3).map((r) => ({ label: r.query, trend: 'up' as const }));
    const top = focalAssoc.top.slice(0, 3).map((r) => ({ label: r.query, trend: 'up' as const }));
    return { positive: rising.length ? rising : top, negative: [] };
  }, [focalAssoc]);

  const topAssociations = useMemo(() => {
    if (!focalAssoc) return [];
    return focalAssoc.top.slice(0, 8).map((r) => r.query);
  }, [focalAssoc]);

  const biggestShift = useMemo(() => {
    if (!data) return null;
    const movers = data.shareOfSearch.brands
      .filter((b) => !hiddenBrands.has(b.brand))
      .map((b) => ({ brand: b.brand, momentum: b.momentum }))
      .sort((a, b) => Math.abs(b.momentum) - Math.abs(a.momentum));
    return movers[0] ?? null;
  }, [data, hiddenBrands]);

  const allBrands = useMemo(
    () => [DEFAULT_CONFIG.brand, ...DEFAULT_CONFIG.competitors],
    []
  );

  // First-load placeholder: show only when we have no data at all.
  // Subsequent refetches (geo change) keep the previous data visible and
  // animate a small spinner in the picker bar instead.
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Computing brand pulse from live signals…
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-red-700 mb-1">Brand Pulse service unavailable</div>
            <div className="text-xs text-red-600">{error ?? 'No data returned.'}</div>
            <div className="text-xs text-gray-600 mt-2">
              Ensure the brand-pulse-service is running (<code>cd brand-pulse-service &amp;&amp; npm run dev</code>)
              or set <code>VITE_BRAND_PULSE_API_URL</code>.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6">
      {/* Geo picker — country or region. Drives the entire fetch. */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Market</span>
          <select
            value={geo}
            onChange={(e) => setGeo(e.target.value)}
            disabled={loading}
            className="text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-300 disabled:opacity-50"
          >
            {geoOptions ? (
              <>
                <optgroup label="Regions">
                  {geoOptions.regions.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label} ({r.countries.length})
                    </option>
                  ))}
                </optgroup>
                {(['north_america', 'uk', 'europe', 'scandinavia', 'south_america'] as const).map((group) => {
                  const items = geoOptions.countries.filter((c) => c.region === group);
                  if (items.length === 0) return null;
                  const groupLabel =
                    geoOptions.regions.find((r) => r.key === group)?.label ?? group;
                  return (
                    <optgroup key={group} label={groupLabel}>
                      {items.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </>
            ) : (
              <option value={geo}>{geo}</option>
            )}
          </select>
          <MetricInfo title="Market scope">
            Choose a single country or a region. Region selections fan out to
            each member country in parallel and aggregate per source: Google
            Ads volumes sum, Wikipedia pageviews sum across language projects,
            Share of Search is volume-weighted, sentiment is sample-weighted,
            etc.
            <br /><br />
            First load of a region is slow (~3–5 minutes for Scandinavia)
            because it pulls 5× the data; subsequent loads are cached.
          </MetricInfo>
        </div>
        {loading && (
          <span className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Refreshing…
          </span>
        )}
      </div>

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {kpiData.map((kpi, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-2 flex items-center gap-1.5">
                <span>{kpi.label}</span>
                <MetricInfo title={kpi.label}>{kpi.info}</MetricInfo>
              </div>
              <div className="flex items-end justify-between mb-3">
                <span className="text-3xl text-gray-900">{kpi.value}</span>
                <span className={`text-sm flex items-center gap-1 ${kpi.delta.positive ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.delta.positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {kpi.delta.label}
                </span>
              </div>
              <div className="h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={kpi.sparkline.map((v) => ({ value: v }))}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={kpi.delta.positive ? '#10b981' : '#ef4444'}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* BPS pillar breakdown — focal brand + cohort comparison */}
      {focalBps && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1.5">
                <h3 className="text-gray-900">Brand Pulse Score — pillar breakdown</h3>
                <MetricInfo title="Pillar breakdown">
                  Each pillar's score is the mean z-score of its sub-metrics across
                  the cohort, mapped to 0–100. <strong>50 = cohort average.</strong>
                  <br /><br />
                  Hover the columns to see the underlying sources. A pillar with
                  no signal (e.g. a sub-metric that's zero across the whole cohort)
                  drops out of the composite and the remaining weights re-normalize.
                </MetricInfo>
              </div>
              <Badge className="bg-purple-50 text-purple-700 border border-purple-200">
                {Math.round(focalBps.confidence * 100)}% confidence
              </Badge>
            </div>
            <div className="grid grid-cols-5 gap-3">
              {(Object.entries(focalBps.pillars) as [keyof typeof focalBps.pillars, typeof focalBps.pillars[keyof typeof focalBps.pillars]][]).map(([name, pillar]) => {
                const labels: Record<string, string> = {
                  awareness: 'Awareness',
                  mentalAvailability: 'Mental Availability',
                  consideration: 'Consideration',
                  sentiment: 'Sentiment',
                  engagement: 'Engagement',
                };
                const weight = focalBps.weightsApplied[name];
                return (
                  <div
                    key={name}
                    className={`p-4 rounded-lg border ${
                      pillar.hasSignal ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-70'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-1">{labels[name]}</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">
                        {pillar.hasSignal ? Math.round(pillar.score) : '—'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {pillar.hasSignal && weight > 0 ? `× ${(weight * 100).toFixed(0)}%` : 'no signal'}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${pillar.hasSignal ? pillar.score : 0}%`,
                          backgroundColor:
                            pillar.score >= 60 ? '#10b981' : pillar.score >= 40 ? '#be43ff' : '#ef4444',
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-400 mt-2 leading-tight">
                      {Object.entries(pillar.sources)
                        .map(([k, v]) => `${k}: ${v.toFixed(1)}σ`)
                        .join(' · ')}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Section: Brand Momentum + Right Panel */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-gray-900">Share of Search — 12-month momentum</h3>
                    <MetricInfo title="Share of Search chart">
                      Weekly Share of Search per brand, normalized to sum to 100% across the
                      cohort. Source: Google Trends interest-over-time.
                      <br /><br />
                      <strong>Reading the chart:</strong> solid lines are "clean"
                      brand terms; dashed lines flag brands whose search volume is
                      polluted by non-category queries (ambiguous names). Use the
                      cohort controls below to hide a brand or flag/unflag it as ambiguous.
                    </MetricInfo>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Weekly Google Trends interest, normalized to 100% across the competitor cohort.
                  </p>
                </div>
                <Badge className="bg-purple-50 text-purple-700 border border-purple-200">
                  {data.meta.geo} · {data.meta.timeframe}
                </Badge>
              </div>

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={momentumData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#9ca3af"
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={30}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      formatter={(v: number) => `${v.toFixed(1)}%`}
                    />
                    {allBrands
                      .filter((brand) => !hiddenBrands.has(brand))
                      .map((brand) => {
                        const isFocal = brand === DEFAULT_CONFIG.brand;
                        const isAmbiguous = ambiguousBrands.has(brand);
                        return (
                          <Line
                            key={brand}
                            type="monotone"
                            dataKey={brand}
                            stroke={BRAND_COLORS[brand] ?? '#94a3b8'}
                            strokeWidth={isFocal ? 3 : 2}
                            strokeDasharray={isAmbiguous ? '5 4' : undefined}
                            dot={false}
                            name={brand}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Interactive cohort control: per-brand visibility + ambiguity toggles */}
              <div className="mt-5 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-medium text-gray-700">Cohort</div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-4 h-[2px] bg-gray-600" />
                      solid = clean
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span
                        className="inline-block w-4 h-[2px] bg-gray-600"
                        style={{ backgroundImage: 'repeating-linear-gradient(90deg, #4b5563 0 4px, transparent 4px 8px)', backgroundColor: 'transparent' }}
                      />
                      dashed = ambiguous term
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allBrands.map((brand) => {
                    const hidden = hiddenBrands.has(brand);
                    const ambiguous = ambiguousBrands.has(brand);
                    const color = BRAND_COLORS[brand] ?? '#94a3b8';
                    return (
                      <div
                        key={brand}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs transition-colors ${
                          hidden ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleHidden(brand)}
                          title={hidden ? `Show ${brand}` : `Hide ${brand}`}
                          className="flex items-center gap-1.5 hover:text-gray-900"
                        >
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: hidden ? 'transparent' : color,
                              border: hidden ? `1.5px dashed ${color}` : 'none',
                            }}
                          />
                          <span className={hidden ? 'text-gray-500 line-through' : brand === DEFAULT_CONFIG.brand ? 'text-gray-900 font-medium' : 'text-gray-700'}>
                            {brand}
                          </span>
                          {hidden ? (
                            <EyeOff className="w-3 h-3 text-gray-400" />
                          ) : (
                            <Eye className="w-3 h-3 text-gray-400" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleAmbiguous(brand)}
                          title={ambiguous ? `Unmark "${brand}" as ambiguous` : `Mark "${brand}" as ambiguous (dashed line)`}
                          className={`ml-1 p-0.5 rounded hover:bg-gray-100 ${
                            ambiguous ? 'text-amber-600' : 'text-gray-300 hover:text-gray-500'
                          }`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-start gap-2 mt-4 text-xs">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">
                  Share of Search is a validated leading indicator of market share
                  (Binet/Hankins, 2020). Ambiguous brand names (dashed) may be
                  inflated by non-category queries — toggle the warning icon to
                  mark any brand as ambiguous, or the eye icon to exclude it
                  from the chart.
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium flex items-center gap-1.5">
                <span>Competitor Benchmark — SoS (trailing 4w)</span>
                <MetricInfo title="Competitor Benchmark">
                  Trailing 4-week Share of Search per brand, sorted descending.
                  Shows who's winning category search attention right now — the
                  focal brand is highlighted in purple.
                  <br /><br />
                  <strong>Tip:</strong> this list is not affected by the chart's
                  hide/ambiguous toggles — hidden brands still appear here so you
                  can see the raw ranking.
                </MetricInfo>
              </h3>
              <div className="space-y-3">
                {competitorBenchmark.map((item) => (
                  <div
                    key={item.brand}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      item.isHighlighted ? 'bg-purple-50 border border-purple-200' : ''
                    }`}
                  >
                    <span className={`text-sm ${item.isHighlighted ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {item.brand}
                    </span>
                    <span className={`text-sm ${item.isHighlighted ? 'text-purple-600 font-bold' : 'text-gray-900 font-medium'}`}>
                      {item.value}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium flex items-center gap-1.5">
                <span>Top Associations</span>
                <MetricInfo title="Top Associations">
                  Google-Trends top related queries for the focal brand over the
                  last 12 months — what people search for alongside the brand name.
                  <br /><br />
                  Think of this as a snapshot of the brand's semantic network:
                  products, attributes, competitors, or use-cases Google most
                  associates with the brand.
                </MetricInfo>
              </h3>
              {topAssociations.length === 0 ? (
                <div className="text-xs text-gray-500">No related queries available.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {topAssociations.map((keyword, index) => (
                    <Badge
                      key={index}
                      className="bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                    >
                      {keyword}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-3 font-medium flex items-center gap-1.5">
                <span>Biggest Shift (30d)</span>
                <MetricInfo title="Biggest Shift">
                  The brand in the <em>visible</em> cohort with the largest absolute
                  change in Share of Search over the last 4 weeks vs. the prior
                  4 weeks.
                  <br /><br />
                  <strong>Positive</strong> = gaining category attention.
                  <strong> Negative</strong> = losing. Recomputes automatically when
                  you toggle brand visibility in the chart above.
                </MetricInfo>
              </h3>
              {biggestShift ? (
                <>
                  <p className="text-sm text-gray-900 mb-2">
                    <span className="font-medium">{biggestShift.brand}</span>{' '}
                    <span className={biggestShift.momentum >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                      {biggestShift.momentum >= 0 ? '+' : '−'}
                      {Math.abs(biggestShift.momentum).toFixed(1)} pts
                    </span>{' '}
                    SoS change in the last 4 weeks vs. prior 4 weeks.
                  </p>
                  <p className="text-xs text-gray-600 mb-4">
                    Computed from Google Trends weekly panel, {data.meta.geo}.
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-600">Not enough data yet.</p>
              )}
              <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 text-sm h-9">
                View Details
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Category Fit Drivers — replaces "Product Drivers" with something we can actually measure */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-gray-900">Category Fit — SERP presence</h3>
                <MetricInfo title="Category Fit — SERP presence">
                  For each top category query, we check whether the brand appears in
                  Google's top-10 organic, "People Also Ask", or Knowledge Panel. The
                  bar = the % of probed queries where the brand surfaces.
                  <br /><br />
                  <strong>Why it matters:</strong> this is a passive, query-level
                  proxy for Ehrenberg-Bass "mental availability" — a brand that
                  doesn't appear in the category SERP is effectively invisible at
                  the moment of buyer intent.
                  <br /><br />
                  Probed queries are listed below the bars.
                </MetricInfo>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Passive proxy for Ehrenberg-Bass mental availability. Share of top
                category queries where the brand surfaces in organic, PAA, or
                Knowledge Panel.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            {data.categoryFit
              .slice()
              .sort((a, b) => b.score - a.score)
              .map((c) => (
                <div key={c.brand} className="flex items-center gap-4">
                  <div className="w-40 text-sm text-gray-900 font-medium">{c.brand}</div>
                  <div className="flex-1 h-5 bg-gray-100 rounded-md overflow-hidden relative">
                    <div
                      className="h-full rounded-md"
                      style={{
                        width: `${c.score}%`,
                        backgroundColor: c.brand === focalBrand ? '#be43ff' : '#cbd5e1',
                      }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm text-gray-900 font-medium">
                    {Math.round(c.score)}%
                  </div>
                </div>
              ))}
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Probed queries: {data.categoryFit[0]?.appearancesByQuery.map((a) => `"${a.query}"`).join(', ')}
          </div>
        </CardContent>
      </Card>

      {/* Insights row */}
      <div>
        <div className="mb-4">
          <div className="flex items-center gap-1.5">
            <h2 className="text-gray-900 mb-1">Brand Tracker Insights</h2>
            <MetricInfo title="Brand Tracker Insights">
              News sentiment (Google News headlines classified by an LLM) and
              trending-query signals per brand. These are the advocacy /
              reputation pillars of the composite score.
            </MetricInfo>
          </div>
          <p className="text-sm text-gray-500">
            News sentiment and trending associations, computed from live signals.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* News mentions bar */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1.5">
                <span>News Mentions (sampled)</span>
                <MetricInfo title="News Mentions">
                  Number of Google News headlines retrieved for the focal brand in
                  this pull (capped at 30). The bar chart shows counts per brand in
                  the cohort — larger ≠ better; it just means more press volume.
                  <br /><br />
                  The <strong>sentiment score</strong> and <strong>headline list</strong>
                  below use this same sample.
                </MetricInfo>
              </div>
              <div className="text-3xl text-gray-900 mb-1">{focalSentiment?.sampleSize ?? 0}</div>
              <div className="text-sm text-gray-500 mb-4">headlines classified, last 24h window</div>
              <div className="h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.sentiment.map((s) => ({
                      brand: s.brand === focalBrand ? s.brand : s.brand.split(' ')[0],
                      mentions: s.sampleSize,
                    }))}
                  >
                    <Bar dataKey="mentions" fill="#be43ff" radius={[4, 4, 0, 0]} />
                    <XAxis dataKey="brand" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sentiment Mix */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium flex items-center gap-1.5">
                <span>Net Sentiment (News)</span>
                <MetricInfo title="Net Sentiment Score">
                  NSS = <code className="bg-gray-800 px-1 rounded">(pos − neg) / total</code>,
                  range −1 to +1. Each headline is classified 3-class
                  (positive / neutral / negative) by Claude Haiku based on how it
                  reflects on the brand.
                  <br /><br />
                  <strong>Calibration:</strong> headline-only, not article body,
                  so short-text classifier ~80% F1 vs. human labels. Treat as
                  directional, not surgical.
                </MetricInfo>
              </h3>
              <div className="flex items-center justify-center mb-2">
                <span className="text-3xl font-bold text-gray-900">
                  {focalSentiment ? (focalSentiment.nss >= 0 ? '+' : '') + focalSentiment.nss.toFixed(2) : '—'}
                </span>
              </div>
              <div className="text-xs text-center text-gray-500 mb-4">
                NSS = (pos − neg) / total
              </div>
              {sentimentMixData.length ? (
                <>
                  <div className="h-28 flex items-center justify-center mb-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sentimentMixData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {sentimentMixData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-gray-600">{focalSentiment?.positive}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-gray-400" />
                      <span className="text-gray-600">{focalSentiment?.neutral}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-gray-600">{focalSentiment?.negative}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-500 text-center">No news data.</div>
              )}
            </CardContent>
          </Card>

          {/* Rising / top queries */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium flex items-center gap-1.5">
                <span>Rising Queries</span>
                <MetricInfo title="Rising Queries">
                  Google-Trends "breakout" or rising related queries for the focal
                  brand over the last 12 months — searches that have grown in volume.
                  <br /><br />
                  Early signal of emerging associations: new products, new use-cases,
                  PR moments, or shifting consumer language around the brand.
                </MetricInfo>
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-2 font-medium">Breakout / rising for {focalBrand}</div>
                  <div className="space-y-1.5">
                    {driversOfChange.positive.length === 0 ? (
                      <div className="text-xs text-gray-500">None available.</div>
                    ) : driversOfChange.positive.map((driver, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 truncate">{driver.label}</span>
                        <TrendingUp className="w-4 h-4 text-green-600 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Top Headlines */}
      {focalSentiment && focalSentiment.topHeadlines.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-gray-900 mb-4 flex items-center gap-1.5">
              <span>Recent News Mentions — {focalBrand}</span>
              <MetricInfo title="Recent News Mentions">
                Sample of Google News headlines for the focal brand, with the
                sentiment badge showing how each was classified (positive /
                neutral / negative). Click through for the source article.
                <br /><br />
                This is the raw evidence behind the Net Sentiment score above —
                use it to sanity-check whether the classifier is reasonable for
                your brand's news cycle.
              </MetricInfo>
            </h3>
            <div className="space-y-3">
              {focalSentiment.topHeadlines.slice(0, 6).map((h, i) => (
                <div key={i} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <Badge
                    className={`flex-shrink-0 ${
                      h.sentiment === 'positive'
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : h.sentiment === 'negative'
                          ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                    }`}
                  >
                    {h.sentiment}
                  </Badge>
                  <div className="flex-1">
                    {h.link ? (
                      <a href={h.link} target="_blank" rel="noreferrer" className="text-sm text-gray-900 hover:text-purple-600">
                        {h.title}
                      </a>
                    ) : (
                      <div className="text-sm text-gray-900">{h.title}</div>
                    )}
                    {h.source && <div className="text-xs text-gray-500 mt-1">{h.source}</div>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-source signals row */}
      <div>
        <div className="mb-4 flex items-center gap-1.5">
          <h2 className="text-gray-900 mb-1">Multi-source signals</h2>
          <MetricInfo title="Multi-source signals">
            Cross-validation pillars used by the composite score. Each card here
            feeds into one or more pillars in the breakdown above. Concordance
            across sources increases score confidence; divergence flags risk.
          </MetricInfo>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Wikipedia pageviews */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-1.5 mb-4">
                <h3 className="text-sm text-gray-900 font-medium">Wikipedia pageviews</h3>
                <MetricInfo title="Wikipedia pageviews">
                  Monthly views of each brand's Wikipedia article (24-month
                  history). Strong, language-disambiguated awareness proxy —
                  unlike search volume, it isn't polluted by surnames or place
                  names because the article slug locks the entity.
                  <br /><br />
                  Source: WMF Pageviews API. Free, no auth.
                </MetricInfo>
              </div>
              <div className="space-y-3">
                {data.wikipedia.map((w) => (
                  <div key={w.brand} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className={`truncate ${w.brand === focalBrand ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                        {w.brand}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {w.article ? w.article.title.replace(/_/g, ' ') : 'no article'}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`${w.brand === focalBrand ? 'text-purple-600 font-bold' : 'text-gray-900 font-medium'}`}>
                        {w.recentAvg >= 1000 ? `${(w.recentAvg / 1000).toFixed(1)}k` : Math.round(w.recentAvg).toLocaleString()}
                      </div>
                      <div className={`text-[11px] ${w.momentum >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {w.momentum >= 0 ? '+' : ''}
                        {w.momentum.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* YouTube engagement */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-1.5 mb-4">
                <h3 className="text-sm text-gray-900 font-medium">YouTube engagement</h3>
                <MetricInfo title="YouTube engagement">
                  For each brand, top 20 videos matching "{'{'}brand{'}'} {'{'}category{'}'}".
                  Views, likes, and comments are summed across the sample.
                  <br /><br />
                  <strong>Total results</strong> = how many videos YouTube
                  reports for the query (awareness signal). <strong>Views/likes/
                  comments</strong> on the sample feed the Engagement pillar.
                </MetricInfo>
              </div>
              <div className="space-y-3">
                {data.youtube.map((y) => (
                  <div key={y.brand} className="flex items-center justify-between gap-2 text-sm">
                    <div className={`min-w-0 flex-1 truncate ${y.brand === focalBrand ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {y.brand}
                    </div>
                    <div className="text-right flex-shrink-0 leading-tight">
                      <div className={`${y.brand === focalBrand ? 'text-purple-600 font-bold' : 'text-gray-900 font-medium'}`}>
                        {y.totalResults >= 1e6 ? `${(y.totalResults / 1e6).toFixed(1)}M` : `${(y.totalResults / 1000).toFixed(0)}k`}
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {y.totalViews >= 1e6 ? `${(y.totalViews / 1e6).toFixed(1)}M` : `${(y.totalViews / 1000).toFixed(0)}k`} views ·{' '}
                        {(y.totalLikes / 1000).toFixed(1)}k likes
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* GDELT news tone */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-1.5 mb-4">
                <h3 className="text-sm text-gray-900 font-medium">GDELT news tone</h3>
                <MetricInfo title="GDELT news tone">
                  Average news-article tone over the last ~30 days from GDELT 2.0,
                  a global open news corpus updated every 15 minutes since 2015.
                  Range roughly −10 to +10 (negative = unfavourable coverage).
                  <br /><br />
                  Empty rows = GDELT didn't surface enough articles for that
                  brand. Free, no auth, but rate-limited (1 req per 5s).
                </MetricInfo>
              </div>
              <div className="space-y-3">
                {data.gdelt.map((g) => (
                  <div key={g.brand} className="flex items-center justify-between gap-2 text-sm">
                    <div className={`min-w-0 flex-1 truncate ${g.brand === focalBrand ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {g.brand}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {g.tone.length === 0 ? (
                        <span className="text-[11px] text-gray-400">no signal</span>
                      ) : (
                        <>
                          <div
                            className={`font-medium ${
                              g.recentTone > 1 ? 'text-green-600' : g.recentTone < -1 ? 'text-red-600' : 'text-gray-700'
                            }`}
                          >
                            {g.recentTone >= 0 ? '+' : ''}
                            {g.recentTone.toFixed(2)}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {g.tone.length} days · {(g.volumeMomentum >= 0 ? '+' : '') + g.volumeMomentum.toFixed(0)}% vol
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Methodology footer */}
      <div className="text-xs text-gray-400 flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          Methodology v{data.meta.methodologyVersion}
          <MetricInfo title="Methodology version">
            The version of the algorithms used to compute this report. Pillar
            formulas, data sources, weights, and known limitations are documented
            in <code className="bg-gray-800 px-1 rounded">BRAND_PULSE_METHODOLOGY.md</code>.
            Stored alongside every datapoint so recomputations are reproducible.
          </MetricInfo>
        </span>
        <span>Computed {new Date(data.meta.computedAt).toLocaleString()}</span>
      </div>
    </div>
    </TooltipProvider>
  );
}
